import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  GetDefaultKeyNamespacesResponse,
  UpdateDefaultKeyNamespacesRequest,
  UpdateDefaultKeyNamespacesResponse,
} from '@nadoutong/contracts';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';

/**
 * `/v1/ops/default-key-namespaces` — the namespace group every newly created
 * tenant API key is given automatic model-mapping rules for (design
 * 2026-08-02). Admin-only; the PUT is a whole-set replacement and is audited
 * on platform-api.
 */

const DEFAULT_KEY_NAMESPACES_KEY = ['default-key-namespaces'] as const;

export function useDefaultKeyNamespaces(
  enabled = true,
): UseQueryResult<GetDefaultKeyNamespacesResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: DEFAULT_KEY_NAMESPACES_KEY,
    queryFn: ({ signal }) =>
      opsApiRequest<GetDefaultKeyNamespacesResponse>(
        '/v1/ops/default-key-namespaces',
        { signal },
        fetchImpl,
      ),
    enabled,
  });
}

export function useUpdateDefaultKeyNamespaces(): UseMutationResult<
  UpdateDefaultKeyNamespacesResponse,
  Error,
  UpdateDefaultKeyNamespacesRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      opsApiRequest<UpdateDefaultKeyNamespacesResponse>(
        '/v1/ops/default-key-namespaces',
        { method: 'PUT', body },
        fetchImpl,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: DEFAULT_KEY_NAMESPACES_KEY });
    },
  });
}
